import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Facebook } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_LINKS, CONTACT_INFO, SOCIAL_LINKS } from "@/data/content";

const SOCIAL_ICONS = { Instagram, Facebook };

const productLinks = [
  { label: "Hybrid Cotton", to: "/products?crop=cotton" },
  { label: "Hybrid Maize", to: "/products?crop=maize" },
  { label: "Hybrid Sunflower", to: "/products?crop=sunflower" },
  { label: "Improved Paddy", to: "/products?crop=improved-paddy" },
  { label: "Hybrid Bajra", to: "/products?crop=bajra" },
  { label: "Research Wheat", to: "/products?crop=research-wheat" },
];

const Footer = () => (
  <footer data-testid="site-footer" className="relative border-t border-white/10 bg-forest-950">
    <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
      <div className="grid gap-12 md:grid-cols-12">
        <div className="md:col-span-5">
          <Logo footer />
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-sage">
            A technology-driven hybrid seed company developing high-performance genetics for higher yields,
            greater adaptability and consistent field performance across Indian agriculture.
          </p>
          <p className="mt-4 text-base italic text-black" style={{ fontFamily: "'Times New Roman', Times, serif" }}>farming simplified...</p>
          <div className="mt-6 flex gap-3">
            {SOCIAL_LINKS.map((s) => {
              const Icon = SOCIAL_ICONS[s.platform];
              return (
                <a
                  key={s.platform}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${s.platform.toLowerCase()}`}
                  aria-label={`Seed Park on ${s.platform}`}
                  className="flex h-9 w-9 items-center justify-center border border-white/10 text-sage transition-colors hover:border-brand-400 hover:text-brand-400"
                >
                  <Icon className="h-4 w-4" />
                </a>
              );
            })}
          </div>
        </div>
        <div className="md:col-span-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-sage">Company</p>
          <ul className="mt-5 space-y-3">
            {NAV_LINKS.slice(1).map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-sm text-mist transition-colors hover:text-brand-400" data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-sage">Portfolio</p>
          <ul className="mt-5 space-y-3">
            {productLinks.map((l) => (
              <li key={l.label}>
                <Link to={l.to} className="text-sm text-mist transition-colors hover:text-brand-400" data-testid={`footer-product-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-3">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-sage">Reach Us</p>
          <ul className="mt-5 space-y-4 text-sm text-mist">
            <li className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-400" />
              5th Floor, NCL Ganga Apartments, above IDBI Bank, Plaza, Bashirabad, Quthbullapur, Secunderabad, Telangana 500067
            </li>
            <li className="flex items-center gap-3">
              <Mail className="h-4 w-4 shrink-0 text-brand-400" />
              <a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-brand-400" data-testid="footer-email">{CONTACT_INFO.email}</a>
            </li>
            <li className="flex items-center gap-3">
              <Phone className="h-4 w-4 shrink-0 text-brand-400" />
              <a href={`tel:${CONTACT_INFO.phone}`} className="hover:text-brand-400" data-testid="footer-phone">{CONTACT_INFO.phone}</a>
            </li>
          </ul>
          <a
            href="https://msgroupin.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-ms-group-link"
            className="group mt-8 inline-flex flex-col items-start gap-2"
            aria-label="Seed Park India is an MS Group company — visit msgroupin.com"
          >
            <span className="inline-block rounded-sm bg-white px-4 py-3">
              <img src="/assets/ms-group-logo.png" alt="MS Group logo" className="h-11 w-auto transition-transform duration-300 group-hover:scale-105" />
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage transition-colors group-hover:text-brand-400">
              An MS Group Company
            </span>
          </a>
        </div>
      </div>
      <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-[#123C6B]/10 pt-8 sm:flex-row sm:items-center">
        <p className="text-xs text-sage">© {new Date().getFullYear()} Seed Park India Private Limited. All rights reserved.</p>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-sage">
          Advanced Genetics • Higher Yields
        </p>
      </div>
    </div>
  </footer>
);

export default Footer;
